import {
  HHPaper,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Typography } from '@mui/material';
import { SystemStyleObject, Theme } from '@mui/system';
import { useCallback, useState } from 'react';
import { useSnackbarContext } from '../../billing-tool/context/snackbar.context';

export const FILE_UPLOAD_DROPZONE_ERROR_TEST_ID = 'file-upload-dropzone-error';

interface FileUploadDropzoneProps {
  value?: File | null;
  onChange: (file: File | null) => void;
  onBlur?: () => void;
  acceptedFileTypes?: string[];
  error?: string;
  maxFileSize?: number;
}

const FileUploadDropzone = ({
  value = null,
  onChange,
  onBlur,
  acceptedFileTypes = ['.csv'],
  error: externalError,
  maxFileSize = 1 * 1024 * 1024, // 1 MB
}: FileUploadDropzoneProps): JSX.Element => {
  const [isDragging, setIsDragging] = useState(false);
  const { setSnackbarState } = useSnackbarContext();
  const [error, setError] = useState<string | null>(null);

  const showError = useCallback(
    (message: string): void => {
      setError(message);
      setSnackbarState(message, 'error', 5000);
    },
    [setSnackbarState],
  );

  const maxFileSizeMb = Math.round(maxFileSize / (1024 * 1024));

  const validateFile = useCallback(
    (file: File): boolean => {
      // Check file type
      const fileType = file.name
        .substring(file.name.lastIndexOf('.'))
        .toLowerCase();
      if (!acceptedFileTypes.includes(fileType)) {
        showError(
          `Invalid file type. Please upload ${acceptedFileTypes.join(
            ', ',
          )} files only.`,
        );
        return false;
      }

      // Check file size
      if (file.size > maxFileSize) {
        showError(
          `File size exceeds ${maxFileSizeMb} MB. Please upload a smaller file.`,
        );
        return false;
      }

      setError(null); // Clear error when validation passes
      return true;
    },
    [acceptedFileTypes, showError, maxFileSize, maxFileSizeMb],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    },
    [],
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    },
    [],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const uploadedFile = e.dataTransfer.files[0];
        if (validateFile(uploadedFile)) {
          onChange(uploadedFile);
        }
      }
      onBlur?.();
    },
    [onChange, onBlur, validateFile],
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (e.target.files && e.target.files.length > 0) {
        const uploadedFile = e.target.files[0];
        if (validateFile(uploadedFile)) {
          onChange(uploadedFile);
        }
      } else {
        onChange(null);
      }
      onBlur?.();
      // Reset the input so the same file can be selected again
      e.target.value = '';
    },
    [onChange, onBlur, validateFile],
  );

  const displayError = error || externalError;

  return (
    <HHStack direction="column" justifyContent="center" gap={2} padding={2}>
      <HHPaper
        elevation={0}
        sx={(theme): SystemStyleObject<Theme> => ({
          width: '350px',
          height: '350px',
          aspectRatio: '1/1',
          border: isDragging ? '3px dashed #3f51b5' : '3px dashed #ccc',
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 4,
          backgroundColor: isDragging
            ? 'rgba(63, 81, 181, 0.05)'
            : theme.palette.background.paper,
          transition: 'all 0.2s ease-in-out',
          cursor: 'pointer',
          margin: 'auto',
        })}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={(): void => document.getElementById('file-input')?.click()}
      >
        <input
          id="file-input"
          type="file"
          accept={acceptedFileTypes.join(',')}
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
        <CloudUploadIcon sx={{ fontSize: 70, color: '#757575', mb: 2 }} />
        {value ? (
          <>
            <HHTypography align="center" hhVariant="h3">
              File Selected: {value.name}
            </HHTypography>
            <Typography
              variant="body2"
              color="textSecondary"
              align="center"
              sx={{ mt: 1 }}
            >
              Click to select a different file
            </Typography>
          </>
        ) : (
          <>
            <HHTypography align="center" hhVariant="h3">
              Drag and drop your CSV file here
            </HHTypography>
            <Typography
              variant="body2"
              color="textSecondary"
              align="center"
              sx={{ mt: 1 }}
            >
              or click to browse files
            </Typography>
          </>
        )}
        <Typography
          variant="caption"
          color="textSecondary"
          align="center"
          sx={{ mt: 2 }}
        >
          Accepted file types: {acceptedFileTypes.join(', ')}
        </Typography>
        <Typography variant="caption" color="textSecondary" align="center">
          Max file size: {maxFileSizeMb} MB
        </Typography>
        {displayError && (
          <Typography
            variant="caption"
            color="error"
            align="center"
            sx={{ mt: 2 }}
            data-testid={FILE_UPLOAD_DROPZONE_ERROR_TEST_ID}
          >
            {displayError}
          </Typography>
        )}
      </HHPaper>
    </HHStack>
  );
};

export default FileUploadDropzone;
